import React, { useEffect, useState, useRef } from 'react';
import {
	Row,
	Col,
	Checkbox,
	Modal,
	Button,
	notification,
	Card,
	Progress,
	Popconfirm,
	Icon,
} from 'antd';
import { request } from '../../utils/http';
import CacheItem from '../../components/cacheItem';
import { clearAllRedisCache, getRedisMemoryUsage } from '../../utils/api';

/**
 * Add new objects to the caches array below to render cache blocks
 *
 * Example:
 *	{
 *		name: 'Tema cache',						<- Name
 *		countUrl: '/path/to/get/cache/count',	<- Path to perform GET count
 *		resetUrl: '/path/to/delete/cache',		<- Path to perform DELETE request
 *		selectDbs: true|false, 					<- Show db selection in modal (Optional)
 *		description: 'Example description',		<- Description to show in modal (Optional)
 *	},
 */

const caches = [
	{
		name: 'Tema cache',
		countUrl: '/admin/lawreports/categories/cache/count',
		resetUrl: '/admin/lawreports/categories/cache',
		selectDbs: true,
	},
	{
		name: 'Kund cache',
		countUrl: '/admin/organization/cache/count',
		resetUrl: '/admin/organization/cache',
		selectDbs: false,
	},
];

const MaintenancePage = ({}) => {
	const [dbs, setDbs] = useState(null);
	const [clearIndex, setClearIndex] = useState(null);
	const [modalVisible, setModalVisible] = useState(false);
	const [modalButtonLoading, setModalButtonLoading] = useState(false);
	const [redisData, setRedisData] = useState(null);

	const cacheItemRefs = useRef([]);

	const updateDbs = async () => {
		const result = await request('GET', '/admin/config');

		if (result && result.databases) {
			// Filter out options that are not used.
			const newDbs = result.databases
				.map((database) => {
					if (!['Alla lagar', 'ÄHO utveckling'].includes(database.name)) {
						return {
							key: database.key,
							name: database.name,
							selected: false,
						};
					}
				})
				.filter((database) => database !== undefined);

			setDbs(newDbs);
		}
	};

	const toggleDb = (index) => {
		const newDbs = [...dbs];
		newDbs[index].selected = !newDbs[index].selected;
		setDbs(newDbs);
	};

	const toggleAllDbs = (selected) => {
		const newDbs = dbs.map((db) => ({
			...db,
			selected,
		}));
		setDbs(newDbs);
	};

	const handleClearClick = (index) => {
		setClearIndex(index);
		setModalVisible(true);
	};

	const resetModal = () => {
		setModalVisible(false);
		setClearIndex(null);
		toggleAllDbs(false);
	};

	const handleSubmitModal = async () => {
		setModalButtonLoading(true);
		const cache = caches[clearIndex];

		let clearUrl = cache.resetUrl;

		if (cache.selectDbs) {
			const dbString = dbs
				.map((db) => (db.selected ? db.key : null))
				.filter((key) => key !== null)
				.join('');

			if (dbString !== '') {
				clearUrl = `${clearUrl}?databases=${dbString}`;
			}
		}

		const result = await request('DELETE', clearUrl);
		setModalButtonLoading(false);

		if (result) {
			notification.success({
				message: `${cache.name} rensad.`,
			});

			// Trigger update for all CacheItems
			cacheItemRefs.current.forEach((ref) => ref.updateCacheCount());
		} else {
			notification.error({
				message: `Rensning av ${cache.name.toLowerCase()} misslyckades.`,
			});
		}

		resetModal();
	};

	const getRedisMemoryData = async () => {
		const result = await getRedisMemoryUsage();

		if (result) {
			setRedisData(result);
		}

		// Trigger update for all CacheItems
		cacheItemRefs.current.forEach((ref) => ref.updateCacheCount());
	};

	const clearAllCache = async () => {
		const result = await clearAllRedisCache();

		if (result) {
			notification.success({
				message: `All redis cache rensad.`,
			});

			// Update redis data
			getRedisMemoryData();

			// Trigger update for all CacheItems
			cacheItemRefs.current.forEach((ref) => ref.updateCacheCount());
		} else {
			notification.error({
				message: `Rensning av all redis cache misslyckades.`,
			});
		}
	};

	useEffect(() => {
		if (!dbs) {
			updateDbs();
		}

		if (!redisData) {
			getRedisMemoryData();
		}
	}, [dbs]);

	return (
		<Row gutter={24}>
			{dbs && clearIndex !== null && (
				<Modal
					title={`Rensa ${caches[clearIndex].name.toLowerCase()}`}
					visible={modalVisible}
					maskClosable={true}
					onCancel={resetModal}
					footer={[
						<Button key='back' onClick={resetModal}>
							Avbryt
						</Button>,
						<Button
							key='submit'
							type='danger'
							icon='delete'
							loading={modalButtonLoading}
							onClick={handleSubmitModal}
						>
							Rensa
						</Button>,
					]}
				>
					{caches[clearIndex].hasOwnProperty('selectDbs') && caches[clearIndex].selectDbs && (
						<Row>
							<Row style={{ marginBottom: 12 }}>
								<Button onClick={() => toggleAllDbs(true)}>Välj alla</Button>
								<Button onClick={() => toggleAllDbs(false)} style={{ marginLeft: 6 }}>
									Töm
								</Button>
							</Row>
							<Row>
								{dbs.map((db, index) => (
									<Col span={12} key={index}>
										<Checkbox
											checked={db.selected}
											onChange={() => toggleDb(index)}
											value={db.name}
										>
											{db.name}
										</Checkbox>
									</Col>
								))}
							</Row>
						</Row>
					)}
					{!caches[clearIndex].selectDbs && caches[clearIndex].description && (
						<p>{caches[clearIndex].description}</p>
					)}
				</Modal>
			)}

			{redisData && (
				<Col span={24} style={{ marginBottom: 24 }}>
					<Card
						title='Redis Status'
						extra={[
							<Popconfirm
								title='Är du säker att du vill rensa ALL cache?'
								onConfirm={clearAllCache}
								okText='Ja'
								cancelText='Nej'
								okType='danger'
								okButtonProps={{ size: 'default' }}
								cancelButtonProps={{ size: 'default' }}
								placement='bottom'
								icon={<Icon type='delete' style={{ color: 'red' }} />}
							>
								<Button type='danger' size='default' icon='delete' style={{ marginRight: 12 }}>
									Rensa allt
								</Button>
							</Popconfirm>,
							<Button type='primary' size='default' icon='reload' onClick={getRedisMemoryData}>
								Uppdatera
							</Button>,
						]}
					>
						<Row>
							<Col xs={24} sm={24} md={12} lg={16} xl={16}>
								<Row>
									<p>
										<b>Nuvarande minnesanvändning:</b> {redisData.used_memory_human}B
									</p>
								</Row>
								<Row>
									<p>
										<b>Högsta minnesanvändning:</b> {redisData.used_memory_peak_human}B
									</p>
								</Row>
								<Row>
									<p>
										<b>Tillgängligt minne:</b> {redisData.maxmemory_human}B
									</p>
								</Row>
							</Col>
							<Col xs={12} sm={12} md={6} lg={4} xl={4}>
								<Row justify='center' type='flex'>
									<Progress
										type='dashboard'
										percent={((redisData.used_memory / redisData.maxmemory) * 100).toFixed(2)}
										status={
											(redisData.used_memory / redisData.maxmemory) * 100 > 100
												? 'exception'
												: 'active'
										}
									/>
								</Row>
								<Row justify='center' type='flex'>
									<h2>Nuvarande</h2>
								</Row>
							</Col>
							<Col xs={12} sm={12} md={6} lg={4} xl={4}>
								<Row justify='center' type='flex'>
									<Progress
										type='dashboard'
										percent={((redisData.used_memory_peak / redisData.maxmemory) * 100).toFixed(2)}
										status={
											(redisData.used_memory_peak / redisData.maxmemory) * 100 > 100
												? 'exception'
												: 'active'
										}
									/>
								</Row>
								<Row justify='center' type='flex'>
									<h2>Högsta</h2>
								</Row>
							</Col>
						</Row>
					</Card>
				</Col>
			)}

			<Col xl={24} style={{ marginBottom: 8 }}>
				<Row type='flex' justify='start' gutter={12}>
					{caches.map((cache, index) => (
						<Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={6} style={{ marginBottom: 12 }}>
							<CacheItem
								ref={(el) => (cacheItemRefs.current[index] = el)}
								key={index}
								name={cache.name}
								label={cache.label}
								countUrl={cache.countUrl}
								onClear={() => handleClearClick(index)}
							/>
						</Col>
					))}
				</Row>
			</Col>
		</Row>
	);
};

export default MaintenancePage;



// WEBPACK FOOTER //
// ./src/pages/maintenance/maintenancePage.jsx